/*
 * Authors: mariushirjanu
 * Created: 21/03/2022
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DashboardMetadataService, IDashboardGroupMetadata, IDashboardMetadata, IDashboardSectionMetadata } from '../dashboard/dashboard-metadata.service';
import { MaxWidth, ResponsiveService } from '../../../shared/responsive/responsive.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { IReportData, IReportResponse, IReportRow, IReportValue, ReportDataService } from '../../../shared/report/report-data.service';
import { STATE_BROWSELIST, STATE_DASHBOARD, STATE_REPORT } from '../../../shared/app-states';
import { BrowseListConfigService, IBrowseListConfig } from '../../../shared/browselists/browselists-config.service';

const REPORT_ID:string = '94';

interface IDashboardProperties {
  numberReports?:number;
  numberGroups?:number;
}

@Component({
  selector: 'dashboard-usage',
  template: `
    <h2>Reports used in the KPI</h2>
    <ol style="padding-left: 15px;">
      <li>
        <a scrollAnchor="distributionReports" href>Distribution of the reports/browselists in the KPIs</a>
      </li>
      <li>
        <a scrollAnchor="overviewReports" href>Overview of the reports/browselists used in KPI sections</a>
        <ul>
          <li *ngFor="let dashboard of dashboards | keyvalue; let isLast = last">
            <a scrollAnchor="dashboard-{{dashboard.key}}" href>{{ dashboard.value.title }}</a>
          </li>
        </ul>
      </li>
    </ol>

    <br/>

    <h3 scrollTarget="distributionReports">1. Distribution of the reports/browselists in the KPIs</h3>
    <table class="table table-bordered table-striped">
      <thead>
        <tr>
          <th>Report/Browselist</th>
          <th>Title</th>
          <td colspan="2" *ngFor="let dashboard of dashboards | keyvalue">
            <a uiSref="${STATE_DASHBOARD}" [uiParams]="{dashboardCode: dashboard.key}" target="_blank">{{ dashboard.value.title }}</a>
          </td>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let report of reports">
          <tr *ngIf="report?.key">
            <td class="text-center">
              <a uiSref="${STATE_REPORT}" [uiParams]="{reportId: report.reportId, showfilters: true}" target="_blank">{{ report.reportId }}</a>
            </td>
            <td>{{ report.name }}</td>
            <td colspan="2" *ngFor="let dashboard of dashboards | keyvalue" class="text-center">
              <span *ngIf="report.usage[dashboard.key]">✔️</span><span *ngIf="report.usage[dashboard.key] > 1"> ({{ report.usage[ dashboard.key ] }})</span>
            </td>
          </tr>
        </ng-container>
        <ng-container *ngFor="let browselist of browselists | keyvalue">
          <tr *ngIf="browselist.value.key">
            <td class="text-center">
              <a uiSref="${STATE_REPORT}" [uiParams]="{reportId: browselist.value.reportId, showfilters: true}" target="_blank">{{ browselist.value.reportId }}</a> /
              <a uiSref="${STATE_BROWSELIST}" [uiParams]="{listCode: browselist.value.browselistId, showfilters: true}" target="_blank">{{ browselist.value.browselistId }}</a>
            </td>
            <td>{{ reports[ browselist.value.reportId ]?.name }}</td>
            <td colspan="2" *ngFor="let dashboard of dashboards | keyvalue" class="text-center">
              <span *ngIf="browselist.value.usage[dashboard.key]">✔️</span><span *ngIf="browselist.value.usage[dashboard.key] > 1"> ({{ browselist.value.usage[ dashboard.key ] }})</span>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>

    <br/><br/>

    <h3 scrollTarget="overviewReports">2. Overview of the reports/browselists used in KPI sections</h3>
    <div style="max-width: 1240px">
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>KPI</th>
            <th>KPI section</th>
            <th style="width: 150px">Report/Browselist</th>
            <th style="width: 360px">Report title</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let dashboard of dashboards | keyvalue" [attr.rowspan]="dashboardProperties[dashboard.key].numberReports" scrollTarget="dashboard-{{dashboard.key}}">
            <td>
              <a uiSref="${STATE_DASHBOARD}" [uiParams]="{dashboardCode: dashboard.key}" target="_blank">{{ dashboard.value.title }} ({{ dashboardProperties[ dashboard.key ].numberReports }})</a>
            </td>
            <td colspan="3">

              <!-- define template for reports -->
              <ng-template #reportsTemplate let-sections="sections" let-group="group">
                <table class="table table-bordered table-striped">
                  <tbody>
                    <tr *ngFor="let section of sections; let idx = index; trackById">
                      <td *ngIf="group && idx === 0" class="bold" [width]="150" [attr.rowspan]="sections.length">
                        <span translate>DASHBOARD.{{ dashboard.key }}.{{ group.key }}.TITLE</span> ({{ sections.length }})
                      </td>
                      <td translate>DASHBOARD.{{ dashboard.key }}.{{ section.key }}.TITLE</td>
                      <td style="width: 150px">
                        <a *ngIf="section.id" uiSref="${STATE_REPORT}" [uiParams]="{reportId: section.id, showfilters: true}" target="_blank">{{ section.id }}</a>
                        <span *ngIf="section.browselistCode">
                          <a uiSref="${STATE_REPORT}" [uiParams]="{reportId: browselists[section.browselistCode].reportId, showfilters: true}" target="_blank">{{ browselists[ section.browselistCode ].reportId }}</a> /
                          <a uiSref="${STATE_BROWSELIST}" [uiParams]="{listCode: section.browselistCode, showfilters: true}" target="_blank">{{ section.browselistCode }}</a>
                        </span>
                      </td>
                      <td style="width: 350px">
                        <span *ngIf="section.id">{{ reports[ section.id ]?.name }}</span>
                        <span *ngIf="section.browselistCode">{{ reports[ browselists[ section.browselistCode ].reportId ]?.name }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </ng-template>

              <!-- display template for dashboard sections with groups -->
              <ng-container *ngFor="let group of dashboard.value.groups">
                <ng-container *ngIf="group.reports" [ngTemplateOutlet]="reportsTemplate" [ngTemplateOutletContext]="{ dashboardKey: dashboard.key, sections: group.reports, group: group }"></ng-container>
              </ng-container>

              <!-- display template for dashboard sections without groups -->
              <ng-container *ngIf="dashboard.value.reports" [ngTemplateOutlet]="reportsTemplate" [ngTemplateOutletContext]="{ dashboardKey: dashboard.key, sections: dashboard.value.reports }"></ng-container>

            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `
})
export class DashboardUsageComponent implements OnInit, OnDestroy {
  public dashboards:{ [ code:string ]:IDashboardMetadata; };
  public dashboardProperties:IDashboardProperties;
  public reports:any = [];
  public browselists:any = [];

  constructor(
    authService:AuthService,
    private dashboardMetadataService:DashboardMetadataService,
    private browseListConfigService:BrowseListConfigService,
    private reportDataService:ReportDataService,
    private responsiveService:ResponsiveService
  ) {
    if (!authService.checkAcslUser()) {
      return;
    }
    responsiveService.width = MaxWidth.LG;
  }

  public ngOnInit():void {
    this.dashboards = _.omit(this.dashboardMetadataService.getMetadataList(), 'TEST');
    this.dashboardProperties = this.processDashboards(this.dashboards);
    this.start();
  }

  private start():void {
    this.reportDataService.loadData(REPORT_ID, REPORT_ID, {}, {})
      .then((report:IReportResponse) => {
        this.calculateReportUsage(report.data);
      });

    this.calculateBrowselistUsage();
  }

  private calculateReportUsage(reportData:IReportData): void {
    _.each(reportData.data, (report:IReportRow) => {
      const reportId:number = util.extractValue(report.Derepid);
      const reportName:string | number | Date | IReportValue = util.extractValue(report.Derepds);

      const reportUsage:number[][] = [];
      reportUsage[reportId] = [];
      this.reports[reportId] = {};

      _.each(this.dashboards, (config:IDashboardMetadata, code:string) => {
        reportUsage[reportId][code] = 0;

        _.each(config.reports, (section:IDashboardSectionMetadata) => {
          if(section.id === reportId){
            reportUsage[reportId][code]++;

            // save section key
            this.reports[reportId] = {
              key: section.key
            };
          }
        });

        _.each(config.groups, (group:IDashboardGroupMetadata) => {
          _.each(group.reports, (section:IDashboardSectionMetadata) => {
            if (section.id === reportId) {
              reportUsage[ reportId ][ code ]++;

              // save section key
              this.reports[ reportId ] = {
                key: section.key
              };
            }
          });
        });
      });

      this.reports[reportId]['reportId'] = reportId;
      this.reports[reportId]['name'] = reportName;
      this.reports[reportId]['usage'] = reportUsage[reportId];
      this.reports[reportId]['usageCounter'] = Object.values(reportUsage[reportId]).reduce((a:number, b:number) => a + b, 0);
    });
  }

  private calculateBrowselistUsage(): void {
    _.each(this.browseListConfigService.getConfigs(), (browselistConfig:IBrowseListConfig, key:string) => {
      const reportName:string = `${browselistConfig.label}`;

      const browselistUsage:number[][] = [];
      browselistUsage[key] = [];
      this.browselists[key] = {};

      _.each(this.dashboards, (config:IDashboardMetadata, code:string) => {
        browselistUsage[key][code] = 0;

        _.each(config.reports, (section:IDashboardSectionMetadata) => {
          if(section.browselistCode === key){
            browselistUsage[key][code]++;

            // save section key
            this.browselists[key] = {
              key: section.key
            };
          }
        });
      });

      this.browselists[key]['browselistId'] = key;
      this.browselists[key]['name'] = reportName;
      this.browselists[key]['usage'] = browselistUsage[key];
      this.browselists[key]['reportId'] = browselistConfig.reportId;
      this.browselists[key]['usageCounter'] = Object.values(browselistUsage[key]).reduce((a:number, b:number) => a + b, 0);
    });
  }

  // calculate the number of total reports and groups
  private processDashboards(dashboards: { [ code:string ]:IDashboardMetadata; }){
    const dashboardProperties:IDashboardProperties = {};

    _.each(dashboards, (dashboard:IDashboardMetadata, key:string) => {
      dashboardProperties[key] = { numberReports: 0, numberGroups: 0 };
      if(dashboard.reports){
        dashboardProperties[key] = { numberReports: dashboard.reports?.length };
      } else {
        const numberReports:number = _.reduce(dashboard.groups, (memo:number, group:IDashboardGroupMetadata) => memo + group.reports?.length, 1);
        dashboardProperties[key] = { numberGroups: dashboard.groups?.length, numberReports };
      }
    });

    return dashboardProperties;
  }

  public ngOnDestroy():void {
    this.responsiveService.width = MaxWidth.SM;
  }
}